/**
 * DV-DEP-001: Schedule to YouTube Modal Component
 *
 * Modal component for scheduling a production to be uploaded to YouTube
 * at a specific date and time with title, description, tags, and visibility settings.
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertCircle, Tag, Eye, Clock, CalendarClock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useCreateYoutubeUpload, useYoutubeIntegration } from '@/hooks/useDeployments';
import { DeploymentsAPIService } from '@/services/deployments-api';
import type { Production, YoutubeUploadFormData } from '@/types/deployments';

interface ScheduleToYouTubeModalProps {
  production: Production;
  onClose: () => void;
}

const ScheduleToYouTubeModal: React.FC<ScheduleToYouTubeModalProps> = ({
  production,
  onClose,
}) => {
  const [formData, setFormData] = useState<YoutubeUploadFormData>({
    title: production.title || '',
    description: production.description || '',
    tags: [],
    visibility: 'unlisted',
    scheduleType: 'later',
    scheduledDate: '',
    scheduledTime: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const createUpload = useCreateYoutubeUpload();
  const { data: integrationData } = useYoutubeIntegration();

  const isConnected = integrationData?.integration?.isConnected ?? false;

  useEffect(() => {
    // Auto-generate title if empty
    if (!formData.title) {
      setFormData(prev => ({
        ...prev,
        title: production.title || `HI-SI Production ${production.id}`,
      }));
    }

    // Set default schedule time to tomorrow at 9 AM
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);

    if (!formData.scheduledDate) {
      setFormData(prev => ({
        ...prev,
        scheduledDate: tomorrow.toISOString().split('T')[0],
        scheduledTime: '09:00',
      }));
    }
  }, [production.title, production.id, formData.title, formData.scheduledDate]);

  const handleInputChange = (field: keyof YoutubeUploadFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors([]); // Clear errors on input change
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 15) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const getScheduledDateTime = (): string | undefined => {
    if (!formData.scheduledDate || !formData.scheduledTime) return undefined;

    // Create date in user's local timezone
    const localDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}:00`);

    // Return ISO string (which converts to UTC)
    return localDateTime.toISOString();
  };

  const validateScheduledTime = (): string[] => {
    const errors: string[] = [];
    const scheduledDateTime = getScheduledDateTime();

    if (!scheduledDateTime) {
      errors.push('Date and time are required for scheduled upload');
      return errors;
    }

    const scheduledDate = new Date(scheduledDateTime);
    const now = new Date();

    if (scheduledDate <= now) {
      errors.push('Scheduled time must be in the future');
    }

    // YouTube requires at least 15 minutes in the future
    const minTime = new Date(now.getTime() + 15 * 60 * 1000);
    if (scheduledDate < minTime) {
      errors.push('Scheduled time must be at least 15 minutes in the future');
    }

    // YouTube scheduling limits (usually up to 6 months)
    const maxTime = new Date(now.getTime() + 6 * 30 * 24 * 60 * 60 * 1000); // ~6 months
    if (scheduledDate > maxTime) {
      errors.push('Scheduled time cannot be more than 6 months in the future');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    const validationErrors = DeploymentsAPIService.validateYoutubeUploadData({
      title: formData.title,
      description: formData.description,
      tags: formData.tags,
      visibility: formData.visibility,
      scheduledAt: getScheduledDateTime(),
    });

    const scheduleErrors = validateScheduledTime();

    const allErrors = [...validationErrors, ...scheduleErrors];

    if (allErrors.length > 0) {
      setErrors(allErrors);
      return;
    }

    try {
      await createUpload.mutateAsync({
        productionId: production.id,
        data: {
          title: formData.title,
          description: formData.description,
          tags: formData.tags,
          visibility: formData.visibility,
          scheduledAt: getScheduledDateTime(),
        },
      });

      onClose();
    } catch (error: any) {
      // Extract server error message if available
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.message ||
                          'Failed to schedule upload. Please try again.';
      setErrors([errorMessage]);
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const getVisibilityDescription = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'Anyone can search for and view';
      case 'unlisted':
        return 'Anyone with the link can view';
      case 'private':
        return 'Only you can view';
      default:
        return '';
    }
  };

  const getPreviewScheduledTime = (): string => {
    if (!formData.scheduledDate || !formData.scheduledTime) return 'Not set';

    // Create date in user's local timezone for display
    const localDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}:00`);

    // Format with timezone info
    return localDateTime.toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getTimezoneDisplay = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  if (!isConnected) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-black border border-red-500/30 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-medium text-white">YouTube Not Connected</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white/80 mb-2">
              You need to connect your YouTube account first
            </p>
            <p className="text-white/60 text-sm">
              Please connect your YouTube account in the integration card above to schedule videos.
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-purple-500/30 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-500/20 border border-purple-500/30 rounded-lg flex items-center justify-center">
              <CalendarClock className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-medium text-white">Schedule YouTube Upload</h2>
              <p className="text-white/60 text-sm">Schedule upload for a specific date and time</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Production Info */}
        <div className="p-6 border-b border-white/10 bg-white/5">
          <div className="flex items-start space-x-4">
            {production.thumbnailUrl && (
              <img
                src={production.thumbnailUrl}
                alt={production.title}
                className="w-24 h-16 object-cover rounded border border-white/20"
              />
            )}
            <div className="flex-1">
              <h3 className="text-white font-medium mb-1">
                {production.title || `Production ${production.id}`}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-white/60 font-mono">
                <span>{production.aspectRatio}</span>
                {production.duration && (
                  <span className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDuration(production.duration)}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Schedule DateTime */}
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
            <h3 className="text-white font-medium mb-4 flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Schedule Settings</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/80 font-medium mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-black/60 border border-purple-500/20 rounded-lg text-white focus:border-purple-500/40 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-white/80 font-medium mb-2">
                  Time *
                </label>
                <input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  className="w-full px-4 py-3 bg-black/60 border border-purple-500/20 rounded-lg text-white focus:border-purple-500/40 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div className="mt-3 p-3 bg-black/40 rounded border border-white/10">
              <p className="text-white/60 text-sm">
                <strong>Scheduled for:</strong> {getPreviewScheduledTime()}
              </p>
              <p className="text-white/40 text-xs mt-1">
                Time zone: {getTimezoneDisplay()} • YouTube requires at least 15 minutes in the future
              </p>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              maxLength={100}
              className="w-full px-4 py-3 bg-black/60 border border-cyan-500/20 rounded-lg text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none"
              placeholder="Enter video title..."
              required
            />
            <div className="flex justify-between mt-1">
              <p className="text-white/40 text-xs">Required for YouTube upload</p>
              <p className="text-white/40 text-xs">{formData.title.length}/100</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              maxLength={5000}
              rows={4}
              className="w-full px-4 py-3 bg-black/60 border border-cyan-500/20 rounded-lg text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none resize-none"
              placeholder="Enter video description..."
            />
            <div className="flex justify-between mt-1">
              <p className="text-white/40 text-xs">Optional description for your video</p>
              <p className="text-white/40 text-xs">{formData.description.length}/5000</p>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Tags ({formData.tags.length}/15)
            </label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-4 py-2 bg-black/60 border border-cyan-500/20 rounded-lg text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none"
                placeholder="Add a tag..."
                disabled={formData.tags.length >= 15}
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={!tagInput.trim() || formData.tags.length >= 15}
                className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Tag className="w-4 h-4" />
              </button>
            </div>

            {/* Tags Display */}
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center space-x-1 px-2 py-1 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded text-sm"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-cyan-400/60 hover:text-cyan-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Visibility
            </label>
            <div className="space-y-2">
              {(['public', 'unlisted', 'private'] as const).map((visibility) => (
                <label
                  key={visibility}
                  className="flex items-start space-x-3 p-3 bg-black/40 border border-white/10 rounded-lg hover:border-white/20 transition-colors cursor-pointer"
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={visibility}
                    checked={formData.visibility === visibility}
                    onChange={(e) => handleInputChange('visibility', e.target.value)}
                    className="mt-1 text-cyan-500 focus:ring-cyan-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-white/60" />
                      <span className="text-white font-medium capitalize">{visibility}</span>
                    </div>
                    <p className="text-white/60 text-sm mt-1">
                      {getVisibilityDescription(visibility)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-red-400 font-medium mb-1">Please fix the following errors:</h4>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="text-red-300 text-sm">
                        • {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-black/60 border border-white/20 text-white/80 rounded-lg hover:bg-black/80 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createUpload.isPending || !formData.title.trim() || !formData.scheduledDate || !formData.scheduledTime}
              className={cn(
                'px-6 py-2 border rounded-lg font-medium transition-colors',
                'flex items-center space-x-2',
                createUpload.isPending || !formData.title.trim() || !formData.scheduledDate || !formData.scheduledTime
                  ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-500/20 border-purple-500/30 text-purple-400 hover:bg-purple-500/30'
              )}
            >
              <Calendar className="w-4 h-4" />
              <span>
                {createUpload.isPending ? 'Scheduling...' : 'Schedule Upload'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleToYouTubeModal;